// src/pages/MotorcycleForm/components/MoneyCellInput.js
import React from "react";
import { Form, InputGroup } from "react-bootstrap";
import { fromCents, toCents } from "../../../utils/money";

function normalizeDisplayInput(raw) {
    // 统一小数点
    const v = String(raw ?? "").replace(",", ".").trim();
    // 允许空/点（代表用户正在输）
    if (v === "" || v === ".") return v;

    // 只允许 0-9 和一个点；最多 2 位小数
    if (!/^\d*\.?\d*$/.test(v)) return null;
    const parts = v.split(".");
    if (parts[1] && parts[1].length > 2) return null;

    return v;
}

function MoneyCellInputImpl({
    valueCents,
    onChangeCents, // (nextCents:number)=>void
    disabled,
    ariaLabel,
    suffix = "€",
    maxWidth = 220,
    inputId,
}) {
    const cents = Number(valueCents ?? 0);

    // 显示值（字符串），只在“非编辑态”跟随外部 cents
    const [display, setDisplay] = React.useState(() => fromCents(cents));
    const isEditingRef = React.useRef(false);

    // 避免每 render 都创建 style 对象
    const wrapStyle = React.useMemo(() => ({ maxWidth }), [maxWidth]);

    React.useEffect(() => {
        if (isEditingRef.current) return;
        // 外部值变了才更新 display
        const next = fromCents(cents);
        setDisplay((prev) => (prev === next ? prev : next));
    }, [cents]);

    const revert = React.useCallback(() => {
        isEditingRef.current = false;
        const next = fromCents(cents);
        setDisplay((prev) => (prev === next ? prev : next));
    }, [cents]);

    const commit = React.useCallback(() => {
        // blur 时提交
        isEditingRef.current = false;

        const raw = String(display ?? "").trim();

        // 空 / "." -> 当 0
        if (raw === "" || raw === ".") {
            if (cents !== 0) onChangeCents(0); // ✅ 不变不提交
            const next = "0.00";
            setDisplay((prev) => (prev === next ? prev : next));
            return;
        }

        const nextCents = toCents(raw);

        // ✅ 不变不提交，避免触发整表更新
        if (nextCents !== cents) onChangeCents(nextCents);

        const nextDisplay = fromCents(nextCents);
        setDisplay((prev) => (prev === nextDisplay ? prev : nextDisplay));
    }, [display, cents, onChangeCents]);

    const onFocus = React.useCallback(() => {
        isEditingRef.current = true;
    }, []);

    const onChange = React.useCallback((e) => {
        const normalized = normalizeDisplayInput(e.target.value);
        if (normalized === null) return; // 非法输入直接忽略
        setDisplay(normalized);
    }, []);

    const onKeyDown = React.useCallback(
        (e) => {
            if (e.key === "Enter") {
                e.preventDefault();
                e.currentTarget.blur(); // blur -> commit
            } else if (e.key === "Escape") {
                e.preventDefault();
                revert();
                e.currentTarget.blur();
            }
        },
        [revert]
    );

    return (
        <InputGroup className="flex-nowrap align-items-stretch" style={wrapStyle}>
            <Form.Control
                id={inputId}
                type="text"
                inputMode="decimal"
                value={display}
                disabled={disabled}
                style={{ minWidth: 0, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
                aria-label={ariaLabel}
                onFocus={onFocus}
                onChange={onChange}
                onKeyDown={onKeyDown}
                onBlur={commit}
            />
            <InputGroup.Text className="text-nowrap">{suffix}</InputGroup.Text>
        </InputGroup>
    );
}

// ✅ 关键：memo，避免父组件更新导致全部单元格重渲染
export default React.memo(MoneyCellInputImpl, (prev, next) => {
    // 如果这些都一样，就不重渲染
    return (
        Number(prev.valueCents ?? 0) === Number(next.valueCents ?? 0) &&
        prev.disabled === next.disabled &&
        prev.ariaLabel === next.ariaLabel &&
        prev.suffix === next.suffix &&
        prev.maxWidth === next.maxWidth &&
        prev.inputId === next.inputId &&
        prev.onChangeCents === next.onChangeCents
    );
});